import { useRef, useState, useEffect } from 'react';

// material
import {
  Stack,
  TextField,
  Typography,
  Button,
  Divider,
  Box,
  CircularProgress,
  Backdrop
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import ReCAPTCHA from 'react-google-recaptcha';
import { setUserProperties } from 'firebase/analytics';
import { useSnackbar } from 'notistack';
import { GoogleAuthProvider, signInWithRedirect } from 'firebase/auth';
import { useGoogleOneTapLogin } from 'react-google-one-tap-login';
import { reportErrorViaWebhook } from '../../../service/webhook.service';
import {
  fetchCitiesForIndia,
  fetchStatesForIndia,
  getUserDetails
} from '../../../service/user.service';
import PhoneInputField from '../../PhoneInputField';
import { sendOtp, verifyOtp } from '../../../service/auth.service';
import { addCke } from '../../../helper/cookieHelper';
import { ADD_GEO_DATA, UPDATE_ORG, USER_LOGIN } from '../../../redux/constants';
import { analytics, auth } from '../../../firebase/firebase';
import Iconify from '../../../fl-components/Iconify';
import { FilterArrayByKey } from '../../../helper/commonHelper';
import { PH_LOGGED_IN } from '../../../utils/posthog.eventNames';
import { capturePHEvents, capturePHIdentify } from '../../../analytics/posthog.analytics';
import { captureGAEvents } from '../../../analytics/google.analytics';
import { GAEvents } from '../../../analytics/events.constants';
// ----------------------------------------------------------------------

const provider = new GoogleAuthProvider();

export default function LoginForm() {
  useGoogleOneTapLogin({
    onError: (error) => console.log(error),
    onSuccess: (response) => {
      const userObj = {
        fullName: response?.name,
        email: response?.email,
        profileImage: response?.picture,
        googleId: response?.sub,
        googleAccessToken: response?.jti
      };
      capturePHEvents('Logged in with one tap gmail', { email: response?.email });
      logInViaGmail(userObj);
    },
    googleAccountConfigs: {
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID
    }
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+91');
  const [displayOTPBar, setDisplayOTP] = useState(false);
  const [otpValue, setOTPvalue] = useState('');
  const reCaptchaRef = useRef();
  const [captchaStates, setCaptchaState] = useState({
    isCaptchLoaded: false,
    captchValue: ''
  });
  const captchaDelay = 1500;
  const { enqueueSnackbar } = useSnackbar();
  // Check login with number
  const [isLoginWithNumber, setIsLoginWithNumber] = useState(false);
  // Show backdrop
  const [showBackdrop, setShowBackdrop] = useState(false);

  useState(() => {
    setTimeout(() => {
      setCaptchaState({ ...captchaStates, isCaptchLoaded: true });
    }, captchaDelay);
  }, []);

  const SITE_KEY = process.env.REACT_APP_CAPTCHA_KEY;

  useEffect(() => {
    setShowBackdrop(true);
    const unsubscribe = auth.onAuthStateChanged((user, error) => {
      if (user) {
        const userObj = {
          fullName: user.displayName,
          email: user.email,
          profileImage: user.photoURL,
          googleId: user.uid,
          googleAccessToken: user.accessToken
        };
        capturePHEvents('Logged in with email', { email: user.email });
        logInViaGmail(userObj);
      } else {
        setShowBackdrop(false);
        auth.signOut();
      }

      // Log error and predefine state
      if (error) {
        enqueueSnackbar('Error while connecting with Google, try another login method', {
          variant: 'error'
        });
        reportErrorViaWebhook(error.message, `Login with gmail: ${error.customData.email}`);
        auth.signOut();
        setShowBackdrop(false);
      }
    });

    return unsubscribe;
  }, []);

  async function handleFormSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const counCode = phoneCountryCode.replace('+', '');
    sendOtp(`${counCode}${phone}`)
      .then(() => {
        enqueueSnackbar('OTP sent to your register number', {
          variant: 'success'
        });
        setCaptchaState({ ...captchaStates, captchValue: '' });
        setDisplayOTP(true);
        setLoading(false);
      })
      .catch(() => {
        enqueueSnackbar('Something went wrong, Please try again later', {
          variant: 'error'
        });
        setLoading(false);
      });
  }

  const getStateAndCities = async () => {
    const stateAndCityRes = await Promise.all([fetchStatesForIndia(), fetchCitiesForIndia()]);

    if (stateAndCityRes.length > 0) {
      dispatch({
        type: ADD_GEO_DATA,
        payload: {
          cities: FilterArrayByKey(stateAndCityRes[1], 'name'),
          states: FilterArrayByKey(stateAndCityRes[0], 'name')
        }
      });
    }
  };

  const handleCaptchaChange = (value) => {
    setCaptchaState({ ...captchaStates, captchValue: value });
    // if value is null recaptcha expired
    if (value === null) setCaptchaState({ ...captchaStates, captchValue: '' });
  };

  const saveUserDataAndRedirect = async (data) => {
    addCke('userId', data.user.id, data.tokens.access.expires);
    addCke('accessToken', data.tokens.access.token, data.tokens.access.expires);
    addCke('refreshToken', data.tokens.refresh.token, data.tokens.refresh.expires);

    // Log event to analytics
    setUserProperties(analytics, { ...data?.user });
    captureGAEvents(GAEvents.login);

    const obj = {
      currentUser: data.user,
      isLogin: true,
      loading: false
    };
    getStateAndCities();
    capturePHEvents(data.user.id);
    capturePHEvents(PH_LOGGED_IN, { userData: data.user });
    dispatch({ type: USER_LOGIN, response: obj });
    // If user comes from join page url
    const joinPageUrl = window.localStorage.getItem('fromEnroll');
    const userRes = await getUserDetails(data.user.id);
    if (userRes) dispatch({ type: UPDATE_ORG, payload: userRes.orgList });
    if (joinPageUrl) {
      navigate(joinPageUrl, { replace: true });
      return;
    }
    if (data.user.isOnboarding) {
      navigate('/pick-user', { replace: true });
    } else {
      navigate('/dashboard/app', { replace: true });
      window.localStorage.removeItem('fromEnroll');
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const counCode = phoneCountryCode.replace('+', '');
    const otpObj = {
      mobileNumber: `${counCode}${phone}`,
      verificationCode: otpValue
    };
    const res = await verifyOtp(otpObj);
    if (res) {
      enqueueSnackbar('OTP verified successfully.', {
        variant: 'success'
      });
      await saveUserDataAndRedirect(res);
      capturePHIdentify(res?.user?.id, {
        email: res?.user?.email,
        name: res?.user?.fullName,
        gender: res?.user?.gender,
        dob: res?.user?.dob,
        mobileNumber: res?.user?.mobileNumber,
        profile: res?.user?.profile
      });
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  /**
   * Login with Gmail
   * @param {Object} userDetails gmail user object
   */
  const logInViaGmail = async (emailObj) => {
    const res = await verifyOtp({ ...emailObj, isGmailLink: true });
    // const res = await getUserFromEmail(userDetails.email);
    if (res) {
      capturePHIdentify(res?.user?.id, {
        email: res?.user?.email,
        name: res?.user?.fullName,
        gender: res?.user?.gender,
        dob: res?.user?.dob,
        mobileNumber: res?.user?.mobileNumber,
        profile: res?.user?.profile
      });
      await saveUserDataAndRedirect(res);
      setShowBackdrop(false);
    } else {
      setShowBackdrop(false);
    }
  };

  const signInWithGoogle = () => {
    signInWithRedirect(auth, provider)
      // .then(() => {
      //   setShowBackdrop(true);
      // })
      .catch((error) => {
        enqueueSnackbar('Error while connecting with Google, try another login method', {
          variant: 'error'
        });
        reportErrorViaWebhook(error.message, `Login with gmail: ${error.customData.email}`);
        setShowBackdrop(false);
      });
  };

  if (isLoginWithNumber) {
    return (
      <>
        {!displayOTPBar ? (
          <form autoComplete="off" noValidate onSubmit={(e) => handleFormSubmit(e)}>
            <Stack spacing={3}>
              <PhoneInputField
                value={phone}
                handleChange={setPhone}
                countryCode={phoneCountryCode}
                handleCountryCode={setPhoneCountryCode}
                autoFocus
                // onBlur={checkUserWithNumber}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ my: 2 }}
            >
              <Typography sx={{ color: 'text.secondary' }}>
                By continuing, you agree to our Terms of Service and Privacy Policy.
              </Typography>
            </Stack>
            {captchaStates.isCaptchLoaded && (
              <ReCAPTCHA
                style={{ margin: '10px 0', display: 'flex', justifyContent: 'center' }}
                ref={reCaptchaRef}
                sitekey={SITE_KEY}
                onChange={handleCaptchaChange}
              />
            )}
            <Stack direction="row" alignItems="center" spacing={2} mt={4}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => setIsLoginWithNumber(false)}
                fullWidth
              >
                Back
              </Button>
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={loading}
                disabled={!(phone.length === 10 && captchaStates.captchValue)}
              >
                Login
              </LoadingButton>
            </Stack>
          </form>
        ) : (
          <form autoComplete="off" noValidate onSubmit={(e) => handleOtpSubmit(e)}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                type="number"
                autoFocus
                value={otpValue}
                label="Enter OTP"
                onChange={(e) => setOTPvalue(e.target.value.toString().slice(0, 6))}
              />
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ my: 2 }}
            >
              <Button
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start'
                }}
                variant="text"
                onClick={() => {
                  setDisplayOTP(false);
                  setOTPvalue('');
                }}
              >
                <div>Edit number</div>
                <div
                  style={{
                    margin: 0,
                    lineHeight: 1.5,
                    fontSize: '14px',
                    color: '#637381'
                  }}
                >
                  {phone}
                </div>
              </Button>
              <Button variant="text" onClick={(e) => handleFormSubmit(e)}>
                Resend
              </Button>
            </Stack>
            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={loading}
              disabled={otpValue.length !== 6}
            >
              Verify OTP
            </LoadingButton>
          </form>
        )}
        {/* Warning dialog if email is changed */}
        {/* <Dialog open={isWarningDialogOpen} onClose={() => setWarningDialogOpen(false)}>
          <DialogContent>
            <Typography>
              You're email would be update with the (<b>{gmailDetails?.email}</b>).
            </Typography>
            <Typography>Are you agree with this?</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={isModalBtnLoading}
              onClick={() => {
                // setGmailDetails(null);
                setUserData(null);
                setPhone('');
                setIsLoginWithNumber(false);
                setDisplayOTP(false);
                setOTPvalue('');
              }}
            >
              Disagree
            </Button>
            <LoadingButton
              variant="contained"
              loading={isModalBtnLoading}
              onClick={(e) => onAgreeChangeEmail(e)}
            >
              Agree
            </LoadingButton>
          </DialogActions>
        </Dialog> */}
      </>
    );
  }

  return (
    <>
      <Button
        startIcon={<Iconify icon="bi:phone-fill" />}
        variant="contained"
        sx={{ width: 'fit-content', margin: '0 auto' }}
        onClick={() => {
          capturePHEvents('Login with mobile number');
          setIsLoginWithNumber(true);
        }}
      >
        Login with OTP
      </Button>
      <Divider sx={{ my: 3 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          OR
        </Typography>
      </Divider>
      <Box
        sx={{
          button: {
            width: 'fit-content !important',
            margin: '0 auto',
            borderRadius: `8px !important`,
            boxShadow: '0 8px 16px 0 rgb(111 110 109 / 15%) !important'
          },
          textAlign: 'center'
        }}
      >
        <Button
          startIcon={<Iconify icon="flat-color-icons:google" />}
          onClick={() => {
            setShowBackdrop(true);
            signInWithGoogle();
          }}
        >
          Login with Google
        </Button>
      </Box>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showBackdrop}
      >
        <CircularProgress />
      </Backdrop>
    </>
  );
}
